import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"

export const AppContext = createContext({
  activeThread: null,
  setActiveThread: () => {},
  threadBubbles: {},
  addThreadBubble: () => {},
  removeThreadBubble: () => {},
})

export const AppConsumer = AppContext.Consumer

export const AppProvider = ({
  children
}) => {
  const [activeThread, setActiveThread] = useState(null)
  const [threadBubbles, setThreadBubbles] = useState({})
  const threadBubblesRef = useRef(threadBubbles)

  const addThreadBubble = useCallback((threadIds, element) => {
    const ids = Array.isArray(threadIds) ? threadIds : [threadIds]

    ids.forEach((id) => {
      const hasThread = threadBubblesRef.current[id]
      const hasElement = threadBubblesRef.current[id]?.includes(element)

      if (hasThread && hasElement) {
        return
      }

      if (!threadBubblesRef.current[id]) {
        threadBubblesRef.current[id] = [element]
        return
      }

      threadBubblesRef.current[id] = [
        ...threadBubblesRef.current[id],
        element,
      ]
    })
    setThreadBubbles({ ...threadBubblesRef.current })
  }, [])

  const removeThreadBubble = useCallback((threadIdOrElement) => {
    const isElement = typeof threadIdOrElement !== "string"
    const isString = typeof threadIdOrElement === "string"

    if (isString) {
      delete threadBubblesRef.current[threadIdOrElement]
      setThreadBubbles({ ...threadBubblesRef.current })
      return
    }

    if (isElement) {
      const element = threadIdOrElement

      Object.keys(threadBubblesRef.current).forEach((id) => {
        const threadBubble = threadBubblesRef.current[id]

        // if the element is included in the thread bubble
        // we'll remove it from the array
        if (threadBubble && threadBubble.includes(element)) {
          threadBubblesRef.current[id] = threadBubble.filter((el) => el !== element)
        }

        // if there are no more elements in the thread bubble
        // we'll remove the thread bubble data
        if (
          threadBubblesRef.current[id] &&
          threadBubblesRef.current[id].length === 0
        ) {
          delete threadBubblesRef.current[id]
        }
      })
      setThreadBubbles({ ...threadBubblesRef.current })
      return
    }
  }, [])

  threadBubblesRef.current = threadBubbles

  const providerValue = useMemo(() => ({
    activeThread,
    setActiveThread,
    threadBubbles,
    addThreadBubble,
    removeThreadBubble,
  }), [
    activeThread,
    setActiveThread,
    threadBubbles,
    addThreadBubble,
    removeThreadBubble,
  ])

  return (<AppContext.Provider value={providerValue}>{children}</AppContext.Provider>);
}

/**
 * Hook to access the app state.
 * @returns {AppContextValue}
 */
export const useAppState = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error("useAppState must be used within an AppProvider")
  }
  return context
}
